import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertCircle, Info, Bookmark, BookmarkCheck, Share2, Upload, Plus, MapPin } from 'lucide-react';
import { useStore } from '../store/useStore';

export type ToastType = 'success' | 'error' | 'info' | 'save' | 'unsave' | 'share' | 'upload' | 'add' | 'location';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { uiTheme } = useStore();
  const themeClass = uiTheme === 'neubrutalism' ? 'theme-neubrutalism' : 'theme-skeuomorphism';

  const showToast = (message: string, type: ToastType = 'success') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className={`fixed bottom-24 sm:bottom-8 left-1/2 -translate-x-1/2 z-[200] flex flex-col gap-2 items-center pointer-events-none ${themeClass}`}>
        <AnimatePresence>
          {toasts.map(toast => (
            <ToastItem key={toast.id} toast={toast} onRemove={removeToast} uiTheme={uiTheme} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: string) => void; uiTheme: string }> = ({ toast, onRemove, uiTheme }) => {
  useEffect(() => {
    const timer = setTimeout(() => onRemove(toast.id), 3000);
    return () => clearTimeout(timer);
  }, [toast.id, onRemove]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success': return <Check className="w-5 h-5" />;
      case 'error': return <X className="w-5 h-5" />;
      case 'info': return <Info className="w-5 h-5" />;
      case 'save': return <BookmarkCheck className="w-5 h-5" />;
      case 'unsave': return <Bookmark className="w-5 h-5" />;
      case 'share': return <Share2 className="w-5 h-5" />;
      case 'upload': return <Upload className="w-5 h-5" />;
      case 'add': return <Plus className="w-5 h-5" />;
      case 'location': return <MapPin className="w-5 h-5" />;
      default: return <Check className="w-5 h-5" />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case 'error': return 'bg-red-600';
      case 'save': return 'bg-brand-600';
      case 'unsave': return 'bg-neutral-700';
      case 'share': return 'bg-green-600';
      case 'upload': return 'bg-purple-600';
      case 'add': return 'bg-brand-600';
      case 'location': return 'bg-green-600';
      default: return 'bg-neutral-900 dark:bg-white dark:text-neutral-900';
    }
  };

  const getToastClass = () => {
    if (uiTheme === 'neubrutalism') {
      return 'border-4 border-black dark:border-white shadow-[4px_4px_0_#000] dark:shadow-[4px_4px_0_#fff] rounded-none';
    }
    return 'border-2 border-amber-800/30 rounded-2xl shadow-premium';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      className={`pointer-events-auto flex items-center gap-3 px-5 py-3 text-white font-medium text-sm ${getBgColor()} ${getToastClass()}`}
    >
      {getIcon()}
      <span>{toast.message}</span>
    </motion.div>
  );
};

export default ToastProvider;
